import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { getYoutubeId, youtubeEmbedUrl } from '@/shared/utils/youtube'

const isExternal = (h?: string) => !!h && /^https?:\/\//.test(h)
const isVideoFile = (s?: string) => !!s && /\.(mp4|webm|ogg)(\?|#|$)/i.test(s)

/** Embebe un video de YouTube de forma responsiva (válido dentro de <p>). */
function YoutubeEmbed({ url }: { url: string }) {
  const embed = youtubeEmbedUrl(url)
  if (!embed) return null
  return (
    <span className="my-4 block aspect-video overflow-hidden rounded-xl border border-line">
      <iframe
        src={embed}
        title="Video"
        className="h-full w-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </span>
  )
}

/**
 * Renderiza Markdown con un estilo limpio (sin depender del plugin typography).
 * Se usa para las páginas centrales editables (Términos, Política de privacidad)
 * y cualquier contenido en Markdown del portal.
 */
export function Markdown({ children }: { children: string }) {
  return (
    <div className="text-[15px] leading-relaxed text-ink">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="mb-4 mt-8 font-serif text-3xl font-normal tracking-tight text-ink first:mt-0">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="mb-3 mt-8 text-xl font-bold text-ink">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="mb-2 mt-6 text-base font-semibold text-ink">{children}</h3>
          ),
          p: ({ children }) => <p className="my-3 text-ink-muted">{children}</p>,
          ul: ({ children }) => <ul className="my-3 list-disc space-y-1.5 pl-5 text-ink-muted">{children}</ul>,
          ol: ({ children }) => <ol className="my-3 list-decimal space-y-1.5 pl-5 text-ink-muted">{children}</ol>,
          li: ({ children }) => <li className="pl-1">{children}</li>,
          a: ({ href, children }) => {
            const text = Array.isArray(children) ? children.join('') : String(children ?? '')
            // Enlace "pelado" de YouTube en su propia línea → se embebe el video.
            if (href && getYoutubeId(href) && text === href) {
              return <YoutubeEmbed url={href} />
            }
            return (
              <a
                href={href}
                {...(isExternal(href) ? { target: '_blank', rel: 'noreferrer' } : {})}
                className="font-medium text-brand underline underline-offset-2 hover:text-brand-dark"
              >
                {children}
              </a>
            )
          },
          img: ({ src, alt }) => {
            const url = typeof src === 'string' ? src : ''
            if (!url) return null
            // ![](enlace-youtube) → video embebido.
            if (getYoutubeId(url)) return <YoutubeEmbed url={url} />
            // ![](archivo.mp4) → reproductor de video.
            if (isVideoFile(url)) {
              return (
                <video
                  src={url}
                  controls
                  className="my-4 w-full rounded-xl border border-line"
                />
              )
            }
            // Imagen normal.
            return (
              <img
                src={url}
                alt={alt ?? ''}
                className="my-4 max-w-full rounded-xl border border-line"
              />
            )
          },
          strong: ({ children }) => <strong className="font-semibold text-ink">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          blockquote: ({ children }) => (
            <blockquote className="my-4 border-l-2 border-brand/40 pl-4 text-ink-muted">{children}</blockquote>
          ),
          hr: () => <hr className="my-6 border-line" />,
          // Un bloque de código (los comandos de `/conector-ia`) no se parte: se
          // desplaza dentro de su caja para no ensanchar la página en un móvil.
          pre: ({ children }) => (
            <pre className="my-4 overflow-x-auto rounded-xl border border-line bg-surface p-4 text-[13px] leading-relaxed">
              {children}
            </pre>
          ),
          code: ({ children }) => (
            <code className="rounded bg-surface px-1.5 py-0.5 text-[13px] text-ink">{children}</code>
          ),
          table: ({ children }) => (
            <div className="my-4 overflow-x-auto">
              <table className="w-full border-collapse text-sm">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border border-line bg-surface px-3 py-2 text-left font-semibold text-ink">{children}</th>
          ),
          td: ({ children }) => <td className="border border-line px-3 py-2 text-ink-muted">{children}</td>,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  )
}
